/**
 * AmcPolish — «Яркость» и «Убрать резкость» в /ai-music-cleaner: звук трека на
 * слух человека, ползунками. Работает и на файле, которому восстанавливать
 * нечего, и поверх восстановленного.
 *
 * ОТКУДА ВЗЯЛОСЬ (08.10.2026, чат 674). До 07.10 Глубокий режим на файле с целым
 * верхом заметно поднимал верхнюю середину — не нейросеть, а ошибка кроссовера
 * (Q у lowpass/highpass Web Audio читается в дБ; разбор — память
 * reference_amc_deep_effect_is_a_crossover_bug_2026_10_07). 07.10 ошибку убрали,
 * и вместе с ней ушёл звук, ради которого люди ходили в инструмент: за 30 дней
 * до починки Глубоким режимом пользовался 221 человек (441 прогон), почти все — на
 * полнополосных песнях из Suno. Премиум-пользователь, прогнавший так 20+ треков,
 * на следующий вечер получил «восстанавливать нечего» и написал «куда делась
 * функция». Решение Фарида 08.10: вернуть — но настраиваемым на слух.
 *
 * ЯРКОСТЬ — пиковый фильтр 10 кГц, Q 0.89. Умолчание +7.5 дБ повторяет прежний
 * подъём по АЧХ: ошибка не больше 0.07 дБ во всём диапазоне 0.5–20 кГц (подбор
 * перебором по частоте, усилению и Q против суммы веток HP² + LP² с добротностью
 * 10^(0.7071/20) = 1.0848; та же кривая замерена 07.10 в Chromium: +7.4 дБ на
 * 10 кГц). В отличие от прежнего кроссовера фильтр минимально-фазовый — без
 * поворота фазы на 360° вокруг 10 кГц, — и после него стоит потолок пиков:
 * прежний путь лимитера не имел, и у горячих мастеров пик уходил до 1.087.
 * Фильтр считается здесь, в JS, а не BiquadFilterNode'ом: одно и то же число на
 * предпрослушке и в файле, без ловушки «Q в дБ» и без OfflineAudioContext.
 *
 * УБРАТЬ РЕЗКОСТЬ — динамический эквалайзер мастеринга (MasteringDynamicEq) в
 * полосе 2–12 кГц: прижимает частоту только в те моменты, когда она выпирает из
 * трека, и не глубже выставленного числа дБ. Это ровно то, что мастеринг делает
 * в «Авто» и «AI-музыке» (там глубина 6). Детектор сравнивает полосу с её же
 * обычным уровнем, поэтому подъём «Яркости» его не обманывает.
 *
 * ПОРЯДОК: копия → яркость → резкость → потолок −0.1 dBFS (AMCQuick.ceiling, тот
 * же владелец, что у Быстрого и Глубокого режимов). Вход не меняется никогда —
 * ползунок можно двигать сколько угодно, исходник для пересчёта цел.
 *
 * API:
 *   BRIGHT, TAME                      — пределы и умолчания ползунков
 *   settings({bright, tame})          → те же числа, зажатые в пределы
 *   isNeutral(s)                      → true, если обработка ничего не меняет
 *   peakingCoefs(sr, f0, q, gainDb)   → [b0, b1, b2, a1, a2]
 *   render(buffer, s, opts)           → Promise<{buffer, deq, ceil, ms}>
 *     opts: {signal, onProgress(0..1), deq, ceiling(channels, sr), makeBuffer(nCh, len, sr)}
 */
(function (root) {
    'use strict';

    var BRIGHT = { f0: 10000, q: 0.89, min: -6, max: 12, step: 0.5, def: 7.5 };
    var TAME = { lo: 2000, hi: 12000, min: 0, max: 12, step: 1, def: 0 };

    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function snap(v, step) { return Math.round(v / step) * step; }
    function num(v, d) { v = +v; return isFinite(v) ? v : d; }

    function settings(s) {
        s = s || {};
        return {
            bright: clamp(snap(num(s.bright, BRIGHT.def), BRIGHT.step), BRIGHT.min, BRIGHT.max),
            tame: clamp(snap(num(s.tame, TAME.def), TAME.step), TAME.min, TAME.max)
        };
    }

    function isNeutral(s) {
        s = settings(s);
        return Math.abs(s.bright) < 1e-6 && s.tame < 1e-6;
    }

    /* Audio EQ Cookbook, peaking. Частота зажата под Найквист записи: у файла
       22.05 кГц 10 кГц — это 0.45 его частоты, и фильтр там ещё честный; ниже
       16 кГц записи центр съезжает к 0.45·sr, чтобы не ломать коэффициенты. */
    function peakingCoefs(sr, f0, q, gainDb) {
        var f = Math.min(f0, 0.45 * sr);
        var w0 = 2 * Math.PI * f / sr, c = Math.cos(w0), al = Math.sin(w0) / (2 * q);
        var A = Math.pow(10, gainDb / 40);
        var a0 = 1 + al / A;
        return [(1 + al * A) / a0, (-2 * c) / a0, (1 - al * A) / a0, (-2 * c) / a0, (1 - al / A) / a0];
    }

    /* Транспонированная прямая форма II в double. Состояние гасится в ноль у
       денормалей: хвост тишины иначе на порядок медленнее. */
    function biquadInPlace(x, co) {
        var b0 = co[0], b1 = co[1], b2 = co[2], a1 = co[3], a2 = co[4];
        var z1 = 0, z2 = 0;
        for (var i = 0, n = x.length; i < n; i++) {
            var v = x[i];
            var y = b0 * v + z1;
            z1 = b1 * v - a1 * y + z2;
            z2 = b2 * v - a2 * y;
            if (z1 > -1e-25 && z1 < 1e-25) z1 = 0;
            if (z2 > -1e-25 && z2 < 1e-25) z2 = 0;
            x[i] = y;
        }
    }

    function defaultMakeBuffer(nCh, len, sr) {
        if (typeof AudioBuffer === 'function') {
            try { return new AudioBuffer({ numberOfChannels: nCh, length: len, sampleRate: sr }); } catch (_) {}
        }
        var OC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
        return new OC(nCh, len, sr).createBuffer(nCh, len, sr);
    }

    function abortErr() {
        var e = new Error('Aborted');
        e.name = 'AbortError';
        return e;
    }

    function yieldTick() {
        if (typeof root.__yieldTick === 'function') return root.__yieldTick(0);
        return new Promise(function (r) { setTimeout(r, 0); });
    }

    async function render(input, s, opts) {
        opts = opts || {};
        s = settings(s);
        var signal = opts.signal || null;
        var onProg = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        var t0 = Date.now();
        function check() { if (signal && signal.aborted) throw abortErr(); }
        function prog(p) { if (onProg) { try { onProg(p); } catch (_) {} } }

        check();
        var nCh = input.numberOfChannels, len = input.length, sr = input.sampleRate;
        var out = (opts.makeBuffer || defaultMakeBuffer)(nCh, len, sr);
        var chans = [], c;
        for (c = 0; c < nCh; c++) {
            var dst = out.getChannelData(c);
            dst.set(input.getChannelData(c));
            chans.push(dst);
        }

        var bright = Math.abs(s.bright) > 1e-6;
        var tame = s.tame > 1e-6 && opts.deq && typeof opts.deq.processAsync === 'function';
        var brightShare = bright ? (tame ? 0.15 : 0.9) : 0;

        if (bright) {
            var co = peakingCoefs(sr, BRIGHT.f0, BRIGHT.q, s.bright);
            for (c = 0; c < nCh; c++) {
                biquadInPlace(chans[c], co);
                prog(brightShare * (c + 1) / nCh);
                await yieldTick(); check();
            }
        }

        var deqRes = null;
        if (tame) {
            var P = opts.deq.resolve({
                mode: 'manual', depth: s.tame, sens: 50, speed: 50, width: 50,
                lo: TAME.lo, hi: TAME.hi
            });
            deqRes = await opts.deq.processAsync(out, P, {
                signal: signal,
                onProgress: function (p) { prog(brightShare + (0.95 - brightShare) * p); }
            });
            check();
        }

        var ceil = null;
        if (typeof opts.ceiling === 'function') {
            try { ceil = opts.ceiling(chans, sr); } catch (_) { ceil = null; }
        }
        prog(1);
        return { buffer: out, deq: deqRes, ceil: ceil, settings: s, ms: Date.now() - t0 };
    }

    var API = {
        BRIGHT: BRIGHT, TAME: TAME,
        settings: settings, isNeutral: isNeutral,
        peakingCoefs: peakingCoefs, render: render,
        _biquadInPlace: biquadInPlace
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (typeof window !== 'undefined') window.AmcPolish = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
